import React, { useState, useEffect } from 'react';
import { walletService } from '../../services/walletService';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Trash2, Plus, Phone, Mail, MessageCircle, Link, FileText } from 'lucide-react';

// Solo los campos que el pase DIBUJA (ver `wallet/src/lib/pass-builder.ts`) **y
// que alguien pueda escribir a mano**. Ofrecer uno que el reverso no renderiza es
// peor que no ofrecerlo: se configura, se guarda y no aparece en ninguna parte.
//
// Dos que el reverso sí dibuja y que NO se piden acá, a propósito:
//   · `poliza_url` — la póliza es un documento por certificado, no por plan, y se
//     genera al momento. No hay un enlace fijo que escribir.
//   · `coberturas_url` — es determinista: flow lo sirve en /coberturas/<plan_id>.
//     Lo arma el snapshot en stempel cuando el plan tiene `json_coberturas`, así
//     apunta siempre al flow del ambiente y nadie lo mantiene a mano.
//
//   siniestro_* / instructivos   → sección TU COMPAÑÍA
//   call_center_phone            → "Call Center" (cae a company_phone)
//   siniestro_whatsapp / _email  → caen a company_whatsapp / company_email
const CAMPOS = [
  'siniestro_web',
  'siniestro_app',
  'siniestro_whatsapp',
  'siniestro_email',
  'instructivos_url',
  'call_center_phone',
  'company_phone',
  'company_whatsapp',
  'company_email',
];

/** Un campo de texto con su etiqueta e icono. Son once, todos iguales. */
function Campo({ icon: Icon, label, placeholder, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </Label>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

const EMPTY_CONFIG = {
  ...Object.fromEntries(CAMPOS.map((c) => [c, ''])),
  help_links: [],
};

/**
 * Editor del reverso del pase, para cualquiera de los dos niveles que se mezclan
 * al armar el snapshot: el PLAN (`planes.wallet_back_config`) o la PÓLIZA
 * COLECTIVA (`polizas_colectivas.wallet_back_config`).
 *
 * El merge es por CLAVE, no por objeto: lo que se deja vacío acá no borra lo que
 * haya en el otro nivel ni en los defaults de la compañía. Por eso los campos
 * vacíos no se guardan.
 *
 * Se pasa `planId` o `polizaColectivaId`, nunca los dos.
 */
export default function BackConfigEditor({ polizaColectivaId, planId }) {
  const esPlan = Boolean(planId);
  const targetId = planId ?? polizaColectivaId;
  const { toast } = useToast();
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!targetId) { setLoading(false); return; }
    setLoading(true);
    const cargar = esPlan
      ? walletService.getPlanBackConfig(targetId)
      : walletService.getBackConfig(targetId);
    cargar
      .then(res => {
        const bc = res.wallet_back_config ?? {};
        setConfig({
          ...Object.fromEntries(CAMPOS.map((c) => [c, bc[c] ?? ''])),
          help_links: bc.help_links ?? [],
        });
      })
      .catch(() => setConfig(EMPTY_CONFIG))
      .finally(() => setLoading(false));
  }, [targetId, esPlan]);

  const setField = (field, value) =>
    setConfig(c => ({ ...c, [field]: value }));

  const addLink = () =>
    setConfig(c => ({ ...c, help_links: [...c.help_links, { label: '', url: '' }] }));

  const updateLink = (idx, key, value) =>
    setConfig(c => ({
      ...c,
      help_links: c.help_links.map((l, i) => i === idx ? { ...l, [key]: value } : l),
    }));

  const removeLink = (idx) =>
    setConfig(c => ({ ...c, help_links: c.help_links.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!targetId) return;
    setSaving(true);
    try {
      // Los vacíos NO se guardan: una clave en '' pisaría el valor del otro nivel
      // (o el default de la compañía) con una cadena vacía, y el reverso mostraría
      // un campo en blanco en vez de caer al valor que sí existe.
      const payload = Object.fromEntries(
        CAMPOS.filter((c) => config[c]?.trim()).map((c) => [c, config[c].trim()])
      );
      payload.help_links = config.help_links.filter(l => l.label.trim() && l.url.trim());

      await (esPlan
        ? walletService.savePlanBackConfig(targetId, payload)
        : walletService.saveBackConfig(targetId, payload));
      toast({
        title: 'Reverso guardado',
        description: 'Los pases ya emitidos se están regenerando.',
      });
    } catch (err) {
      toast({ title: 'Error al guardar', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!targetId) {
    return (
      <div className="flex items-center justify-center h-28 text-sm text-muted-foreground rounded-lg border border-dashed">
        {esPlan ? 'Guarda el plan antes de configurar el reverso' : 'Sin póliza colectiva asociada'}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-28 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Cargando configuración…
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2">

      {/* Los títulos espejan las secciones del reverso del pase, para que se vea
          dónde va a salir cada campo. */}

      {/* TU COMPAÑÍA */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Tu compañía · siniestros
          </p>
          <div className="grid grid-cols-1 gap-3">
            <Campo
              icon={Link}
              label="Sitio de siniestros"
              placeholder="https://compania.cl/siniestros"
              value={config.siniestro_web}
              onChange={v => setField('siniestro_web', v)}
            />
            <Campo
              icon={Link}
              label="App de siniestros"
              placeholder="https://..."
              value={config.siniestro_app}
              onChange={v => setField('siniestro_app', v)}
            />
            <Campo
              icon={MessageCircle}
              label="WhatsApp siniestros"
              placeholder="+56 9 XXXX XXXX"
              value={config.siniestro_whatsapp}
              onChange={v => setField('siniestro_whatsapp', v)}
            />
            <Campo
              icon={Mail}
              label="Email siniestros"
              placeholder="siniestros@compania.cl"
              value={config.siniestro_email}
              onChange={v => setField('siniestro_email', v)}
            />
            <Campo
              icon={FileText}
              label="Instructivos"
              placeholder="https://..."
              value={config.instructivos_url}
              onChange={v => setField('instructivos_url', v)}
            />
            <Campo
              icon={Phone}
              label="Call center"
              placeholder="+56 2 XXXX XXXX"
              value={config.call_center_phone}
              onChange={v => setField('call_center_phone', v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Contacto general — respaldo de los tres campos de arriba */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Contacto general de la compañía
          </p>
          <p className="text-xs text-muted-foreground">
            Se usan solo si los de siniestros están vacíos.
          </p>
          <div className="grid grid-cols-1 gap-3">
            <Campo
              icon={Phone}
              label="Teléfono"
              placeholder="+56 2 XXXX XXXX"
              value={config.company_phone}
              onChange={v => setField('company_phone', v)}
            />
            <Campo
              icon={MessageCircle}
              label="WhatsApp"
              placeholder="+56 9 XXXX XXXX"
              value={config.company_whatsapp}
              onChange={v => setField('company_whatsapp', v)}
            />
            <Campo
              icon={Mail}
              label="Email"
              placeholder="contacto@compania.cl"
              value={config.company_email}
              onChange={v => setField('company_email', v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Links de ayuda */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Links de ayuda
            </p>
            <Button type="button" variant="outline" size="sm" onClick={addLink}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Agregar
            </Button>
          </div>

          {config.help_links.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">
              Sin links — ej: "Red de farmacias", "Asistencia vial 24/7"
            </p>
          ) : (
            <div className="space-y-2">
              {config.help_links.map((link, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    placeholder="Etiqueta"
                    className="flex-1"
                    value={link.label}
                    onChange={e => updateLink(idx, 'label', e.target.value)}
                  />
                  <div className="relative flex-[2]">
                    <Link className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                    <Input
                      placeholder="https://..."
                      className="pl-7"
                      value={link.url}
                      onChange={e => updateLink(idx, 'url', e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    onClick={() => removeLink(idx)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#007D6E] hover:bg-[#006558]"
        >
          {saving
            ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Guardando…</>
            : 'Guardar configuración'
          }
        </Button>
      </div>
    </div>
  );
}
